/* Interactive gallery, image zoom, keyboard-accessible tabs, feelings check-in, breathing activity and sticky purchase bar. No libraries required. */
(() => {
  if (customElements.get("bloom-editable")) return;
  const editorStates = new Map();
  class BloomEditable extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.controller = new AbortController();
      this.state = editorStates.get(this.dataset.sectionId) || {tabs: {}, slide: 0, variant: null};
      editorStates.set(this.dataset.sectionId, this.state);
      this.slides = [...this.querySelectorAll(".bl-gallery-slide")];
      this.index = 0;
      this.timer = null;
      this.running = false;
      this.done = false;
      this.feeling = "";
      this.dialog = this.querySelector("dialog");
      this.on(this, "click", (event) => this.handleClick(event));
      this.on(this, "keydown", (event) => this.handleKeys(event));

      this.on(this.dialog, "close", () => { if (this.lastFocused?.isConnected) this.lastFocused.focus(); });
      this.on(this.dialog, "click", (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (
          event.clientX < rect.left ||
          event.clientX > rect.right ||
          event.clientY < rect.top ||
          event.clientY > rect.bottom
        )
          this.closeZoom();
      });
      this.on(document, "visibilitychange", () => {
        if (document.hidden && this.running) this.resetBreathing();
      });
      const viewport = this.querySelector(".bl-gallery-viewport");
      this.on(viewport, "pointerdown", (event) => {
        this.startPointer = { x: event.clientX, y: event.clientY };
      });
      this.on(viewport, "pointerup", (event) => {
        if (!this.startPointer) return;
        const dx = event.clientX - this.startPointer.x,
          dy = event.clientY - this.startPointer.y;
        this.startPointer = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
          this.suppressZoomUntil = Date.now() + 350;
          this.showSlide(this.index + (dx < 0 ? 1 : -1));
        }
      });
      this.on(viewport, "pointercancel", () => {
        this.startPointer = null;
      });
      this.showSlide(this.state.slide || 0);
      this.querySelectorAll('[data-bl-tabs]').forEach(group => {
        const name = group.classList.contains('bl-tool-tabs') ? 'tools' : 'routine';
        const tabs = [...group.querySelectorAll('[data-tab]')];
        const active = tabs.find(tab => tab.dataset.tab === this.state.tabs[name]) || tabs[0];
        if (active) this.selectTab(active);
      });
      const picker = this.querySelector('[data-bl-variant-picker]');
      if (picker && this.state.variant && [...picker.options].some(option => option.value === this.state.variant)) {
        picker.value = this.state.variant;
        this.updateVariant(picker.selectedOptions[0]);
      }
      this.on(document, 'shopify:block:select', event => {
        const block = [...this.querySelectorAll('[data-bl-block]')].find(node => node.dataset.blBlock === event.detail?.blockId);
        if (!block) return;
        if (block.matches('.bl-sticky-offer')) {
          this.stickySelected = true;
          this.setSticky(true);
        }
      });
      this.on(document, 'shopify:block:deselect', () => {
        this.stickySelected = false;
        this.setSticky(this.stickyVisible || false);
      });
      this.on(this.querySelector('[data-bl-variant-picker]'), 'change', (event) => this.updateVariant(event.target.selectedOptions[0]));
      const hero = this.querySelector(".bl-hero");
      if (hero && "IntersectionObserver" in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            const visible =
              !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.stickyVisible = visible;
            this.setSticky(visible || this.stickySelected);
          },
          { threshold: 0 },
        );
        this.observer.observe(hero);
      } else if (!hero) {
        this.setSticky(true);
      }
    }
    setSticky(visible) {
      const sticky = this.querySelector('.bl-sticky-offer');
      if (!sticky) return;
      sticky.classList.toggle('is-visible', Boolean(visible));
      sticky.setAttribute('aria-hidden', String(!visible));
      sticky.toggleAttribute('inert', !visible);
    }
    updateVariant(option) {
      if (!option) return;
      this.state.variant = option.value;
      const available = option.dataset.available === 'true';
      const sale = option.dataset.sale === 'true';
      this.querySelectorAll('[data-bl-variant-id]').forEach(input => { input.value = option.value; });
      this.querySelectorAll('[data-bl-price]').forEach(node => {
        node.textContent = option.dataset.price;
        if (node.hasAttribute('data-bl-with-currency')) {
          const currency = document.createElement('span');
          currency.textContent = ' ' + this.dataset.currency;
          node.append(currency);
        }
      });
      this.querySelectorAll('[data-bl-cta-price]').forEach(node => { node.textContent = option.dataset.price; });
      this.querySelectorAll('[data-bl-compare]').forEach(node => { node.textContent = option.dataset.compare; node.hidden = !sale; });
      this.querySelectorAll('[data-bl-saving]').forEach(node => { node.textContent = (this.dataset.savingLabel || '') + ' ' + option.dataset.saving; node.hidden = !sale; });
      this.querySelectorAll('[data-bl-product-form]').forEach(form => {
        form.querySelector('[type="submit"]').disabled = !available;
        form.querySelector('[data-bl-buy-label]').hidden = !available;
        form.querySelector('[data-bl-sold-label]').hidden = available;
      });
    }
    on(target, name, callback) {
      target?.addEventListener(name, callback, {
        signal: this.controller.signal,
      });
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      this.controller?.abort();
      this.observer?.disconnect();
      if (this.dialog?.open) this.dialog.close();
      this._connected = false;
    }
    handleClick(event) {
      const button = event.target.closest("button");
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute("data-gallery-direction"))
        this.showSlide(
          this.index + Number(button.dataset.galleryDirection),
        );
      if (button.hasAttribute("data-gallery-index"))
        this.showSlide(Number(button.dataset.galleryIndex));
      if (
        button.hasAttribute("data-zoom") &&
        Date.now() > (this.suppressZoomUntil || 0)
      )
        this.openZoom(Number(button.dataset.zoom), button);
      if (button.hasAttribute("data-close-zoom")) this.closeZoom();
      if (button.hasAttribute("data-tab")) this.selectTab(button);
      if (button.hasAttribute("data-feeling"))
        this.chooseFeeling(button.dataset.feeling);
      if (button.hasAttribute("data-breath-control"))
        this.running ? this.resetBreathing() : this.beginBreathing();
    }
    showSlide(value) {
      if (!this.slides.length) return;
      this.index = (value + this.slides.length) % this.slides.length;
      this.state.slide = this.index;
      this.querySelector(".bl-gallery-track").style.transform =
        `translateX(-${this.index * 100}%)`;
      this.slides.forEach((slide, i) => {
        slide.setAttribute("aria-hidden", String(i !== this.index));
        slide.toggleAttribute("inert", i !== this.index);
      });
      this.querySelectorAll("[data-gallery-index]").forEach(
        (button, i) => {
          button.classList.toggle("active", i === this.index);
          button.setAttribute("aria-pressed", String(i === this.index));
        },
      );
    }
    selectTab(button) {
      const group = button.closest("[data-bl-tabs]");
      const groupName = group.classList.contains('bl-tool-tabs') ? 'tools' : 'routine';
      this.state.tabs[groupName] = button.dataset.tab;
      group.querySelectorAll("[data-tab]").forEach((tab) => {
        const active = tab === button;
        tab.setAttribute("aria-selected", String(active));
        tab.tabIndex = active ? 0 : -1;
        tab.toggleAttribute("data-active", active);
      });
      group
        .querySelectorAll("[data-panel]")
        .forEach(
          (panel) =>
            (panel.hidden = panel.dataset.panel !== button.dataset.tab),
        );
    }
    handleKeys(event) {
      const tab = event.target.closest('[role="tab"]');
      if (
        tab &&
        ["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)
      ) {
        event.preventDefault();
        const tabs = [
          ...tab
            .closest('[role="tablist"]')
            .querySelectorAll('[role="tab"]'),
        ];
        let index = tabs.indexOf(tab);
        index =
          event.key === "Home"
            ? 0
            : event.key === "End"
              ? tabs.length - 1
              : (index +
                  (event.key === "ArrowRight" ? 1 : -1) +
                  tabs.length) %
                tabs.length;
        this.selectTab(tabs[index]);
        tabs[index].focus();
        return;
      }
      if (
        event.target.closest(".bl-gallery-viewport") &&
        ["ArrowLeft", "ArrowRight"].includes(event.key)
      ) {
        event.preventDefault();
        this.showSlide(
          this.index + (event.key === "ArrowRight" ? 1 : -1),
        );
      }
      if (this.dialog?.open && event.key === "Escape") {
        event.preventDefault();
        this.closeZoom();
      }
    }
    openZoom(index, button) {
      const source = this.slides[index]?.querySelector("img");
      if (!source) return;
      this.lastFocused = button;
      const image = this.dialog.querySelector("img");
      image.src = source.src;
      image.alt = source.alt;
      const caption = this.querySelectorAll('[data-gallery-index]')[index];
      this.dialog.querySelector('h2').textContent = caption ? caption.textContent.trim() : source.alt;
      this.dialog.showModal();
      this.dialog.querySelector("[data-close-zoom]").focus();
    }
    closeZoom() {
      this.dialog.close();
    }
    chooseFeeling(name) {
      const config = this.querySelector('[data-bl-practice-config]')?.dataset;
      if (!config) return;
      const prompts = {Worried: config.worriedPrompt, Frustrated: config.frustratedPrompt, Sad: config.sadPrompt, Happy: config.happyPrompt};
      if (!prompts[name]) return;
      this.feeling = name;
      this.resetBreathing();
      this.querySelectorAll("[data-feeling]").forEach((button) => {
        const active = button.dataset.feeling === name;
        button.classList.toggle("selected", active);
        button.setAttribute("aria-pressed", String(active));
      });
      const response = this.querySelector(".bl-mood-response");
      const heading = document.createElement("strong");
      const selectedButton = [...this.querySelectorAll('[data-feeling]')].find(button => button.dataset.feeling === name);
      const label = selectedButton ? [...selectedButton.childNodes].filter(node => node.nodeType === Node.TEXT_NODE).map(node => node.textContent).join('').trim() : name;
      heading.textContent = `${config.feelingPrefix} ${label.toLowerCase()}.`;
      const copy = document.createElement("p");
      copy.textContent = prompts[name];
      response.replaceChildren(heading, copy);
    }
    resetBreathing() {
      clearInterval(this.timer);
      this.timer = null;
      this.running = false;
      this.done = false;
      this.ticks = 0;
      this.updateBreathing();
    }
    beginBreathing() {
      clearInterval(this.timer);
      this.ticks = 0;
      this.done = false;
      this.running = true;
      this.updateBreathing();
      this.timer = setInterval(() => {
        this.ticks++;
        if (this.ticks >= 18) {
          clearInterval(this.timer);
          this.timer = null;
          this.running = false;
          this.done = true;
        }
        this.updateBreathing();
      }, 1000);
    }
    updateBreathing() {
      const circle = this.querySelector(".bl-breath-circle");
      const config = this.querySelector('[data-bl-practice-config]')?.dataset;
      if (!circle || !config) return;
      circle.classList.toggle("is-breathing", this.running);
      circle.querySelector("strong").textContent = this.done
        ? config.doneTitle
        : this.running
          ? Math.floor(this.ticks / 3) % 2 === 0
            ? config.inTitle
            : config.outTitle
          : config.readyTitle;
      circle.querySelector("span").textContent = this.done
        ? config.doneNote
        : this.running
          ? config.runningNote
          : config.readyNote;
      const button = this.querySelector("[data-breath-control]");
      const svg = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "svg",
      );
      for (const [name, value] of Object.entries({
        width: "16",
        height: "16",
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        "stroke-width": "2",
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
        "aria-hidden": "true",
      }))
        svg.setAttribute(name, value);
      const icon = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path",
      );
      icon.setAttribute(
        "d",
        this.running
          ? "M9 5v14M15 5v14"
          : this.done
            ? "M3 10a9 9 0 1 1 2 8M3 4v6h6"
            : "m6 3 14 9-14 9V3",
      );
      svg.append(icon);
      button.replaceChildren(
        svg,
        document.createTextNode(
          this.running
            ? ' ' + config.stopLabel
            : this.done
              ? ' ' + config.againLabel
              : ' ' + config.beginLabel,
        ),
      );
      this.querySelector(".bl-breath-controls>span").textContent = this
        .running
        ? `${Math.min(3, Math.floor(this.ticks / 6) + 1)} of 3 breaths`
        : config.comfortNote;
      this.querySelector(".bl-pause-done")?.remove();
      if (this.done) {
        const message = document.createElement("p");
        message.className = "bl-pause-done";
        message.setAttribute("aria-live", "polite");
        message.textContent =
          config.completion;
        this.querySelector(".bl-practice-card").append(message);
      }
    }
  }
  customElements.define("bloom-editable", BloomEditable);
})();
